import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { departmentService, CreateDepartmentPayload } from '../departmentService';

/** All departments (used for dropdowns/selects). */
export function useDepartments() {
  return useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentService.getAllDepartments(),
    staleTime: 30000,
  });
}

/** Paginated + searchable departments (used by the list page). */
export function usePaginatedDepartments(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['departments', 'paginated', page, limit, search],
    queryFn: () => departmentService.getDepartments(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDepartmentPayload) => departmentService.createDepartment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDepartmentPayload> }) =>
      departmentService.updateDepartment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => departmentService.deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

/** Bulk-delete departments as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await departmentService.deleteDepartment(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}
